import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../auth/supabaseClient";
import {
  buildTankWaterProfileSetOperation,
  buildWaterCircuitUpsertOperation,
  type TankWaterMode,
  type WaterCircuitKind,
} from "../operations/waterTopologyOperations";
import { getOrCreateDeviceId } from "../sync/deviceIdentity";
import { persistAndSyncOperation } from "../sync/operationRuntime";
import type { OfflineOperation, OutboxSummary } from "../sync/types";
import { ConfirmationSheet } from "./ConfirmationSheet";

interface WaterTopologyAdministrationPanelProps {
  farmId: string;
  userId: string;
  onSyncSummary?: (summary: OutboxSummary) => void;
}

interface WaterCircuitAdminRow {
  circuitId: string;
  circuitCode: string;
  displayName: string;
  circuitKind: WaterCircuitKind;
  nominalVolumeL?: number;
  isActive: boolean;
  domainVersion: number;
  connectedTankCount: number;
}

interface TankAdminRow {
  tankId: string;
  tankCode: string;
  mode?: TankWaterMode;
  circuitId?: string;
  independentReason?: string;
  domainVersion: number;
}

interface WaterTopologySnapshot {
  canManage: boolean;
  circuits: WaterCircuitAdminRow[];
  tanks: TankAdminRow[];
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function rows(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.map(record) : [];
}

function firstRecord(value: unknown): Record<string, unknown> | undefined {
  if (Array.isArray(value)) return record(value[0]);
  if (value && typeof value === "object") return record(value);
  return undefined;
}

function optionalString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function optionalNumber(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return undefined;
}

function circuitKind(value: unknown): WaterCircuitKind {
  if (value === "shared_loop" || value === "other") return value;
  return "shared_sump";
}

function tankMode(value: unknown): TankWaterMode | undefined {
  return value === "connected" || value === "independent" ? value : undefined;
}

async function loadWaterTopology(farmId: string): Promise<WaterTopologySnapshot> {
  const [permissionResult, circuitResult, tankResult, stateResult] = await Promise.all([
    supabase.rpc("permission_decide", {
      target_farm_id: farmId,
      requested_permission: "water.manage",
    }),
    supabase.rpc("water_circuit_admin_list_v1", {
      target_farm_id: farmId,
    }),
    supabase
      .from("tanks")
      .select("id,tank_code,is_active")
      .eq("farm_id", farmId)
      .eq("is_active", true)
      .order("tank_code"),
    supabase
      .from("tank_water_states")
      .select("tank_id,mode,circuit_id,independent_reason,domain_version")
      .eq("farm_id", farmId),
  ]);

  if (permissionResult.error) throw permissionResult.error;
  if (circuitResult.error) throw circuitResult.error;
  if (tankResult.error) throw tankResult.error;
  if (stateResult.error) throw stateResult.error;

  const permission = firstRecord(permissionResult.data);
  const stateByTank = new Map(
    rows(stateResult.data).map((state) => [String(state.tank_id), state]),
  );

  const circuits = rows(circuitResult.data).map((item) => {
    const volume = optionalNumber(item.nominal_volume_l);

    return {
      circuitId: String(item.circuit_id),
      circuitCode: String(item.circuit_code ?? ""),
      displayName: String(item.display_name ?? ""),
      circuitKind: circuitKind(item.circuit_kind),
      ...(volume === undefined ? {} : { nominalVolumeL: volume }),
      isActive: item.is_active === true,
      domainVersion: Number(item.domain_version ?? 0),
      connectedTankCount: Number(item.connected_tank_count ?? 0),
    } satisfies WaterCircuitAdminRow;
  });

  const tanks = rows(tankResult.data).map((item) => {
    const tankId = String(item.id);
    const state = stateByTank.get(tankId);
    const mode = tankMode(state?.mode);
    const connectedCircuitId = optionalString(state?.circuit_id);
    const reason = optionalString(state?.independent_reason);

    return {
      tankId,
      tankCode: String(item.tank_code ?? ""),
      ...(mode ? { mode } : {}),
      ...(connectedCircuitId ? { circuitId: connectedCircuitId } : {}),
      ...(reason ? { independentReason: reason } : {}),
      domainVersion: Number(state?.domain_version ?? 0),
    } satisfies TankAdminRow;
  });

  return {
    canManage: permission?.allowed === true,
    circuits,
    tanks,
  };
}

function kindLabel(kind: WaterCircuitKind): string {
  if (kind === "shared_loop") return "مدار مشترک";
  if (kind === "other") return "سایر";
  return "سامپ مشترک";
}

function syncMessage(status: string): string {
  if (status === "synced") return "تغییر مدار آب همگام شد.";
  if (status === "pending") return "تغییر در صف آفلاین ثبت شد.";
  if (status === "retryable_error") return "تغییر حفظ شد و دوباره برای سرور ارسال می‌شود.";
  if (status === "terminal_conflict")
    return "نسخه داده تغییر کرده؛ وضعیت را تازه‌سازی و دوباره بررسی کنید.";
  return "تغییر ثبت شد.";
}

export function WaterTopologyAdministrationPanel({
  farmId,
  userId,
  onSyncSummary,
}: WaterTopologyAdministrationPanelProps) {
  const [snapshot, setSnapshot] = useState<WaterTopologySnapshot>();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>();
  const [error, setError] = useState<string>();
  const [pending, setPending] = useState<OfflineOperation>();
  const [confirmationParts, setConfirmationParts] = useState<string[]>([]);

  const [editingCircuitId, setEditingCircuitId] = useState<string>();
  const [circuitCode, setCircuitCode] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [circuitKindValue, setCircuitKindValue] = useState<WaterCircuitKind>("shared_sump");
  const [nominalVolume, setNominalVolume] = useState("");
  const [circuitActive, setCircuitActive] = useState(true);

  const [selectedTankId, setSelectedTankId] = useState("");
  const [profileMode, setProfileMode] = useState<TankWaterMode>("connected");
  const [profileCircuitId, setProfileCircuitId] = useState("");
  const [independentReason, setIndependentReason] = useState("");

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(undefined);
      const next = await loadWaterTopology(farmId);
      setSnapshot(next);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "بارگذاری مدارهای آب انجام نشد.");
    } finally {
      setLoading(false);
    }
  }, [farmId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const selectedTank = useMemo(
    () => snapshot?.tanks.find((tank) => tank.tankId === selectedTankId),
    [selectedTankId, snapshot?.tanks],
  );

  useEffect(() => {
    if (!selectedTank) return;

    setProfileMode(selectedTank.mode ?? "connected");
    setProfileCircuitId(selectedTank.circuitId ?? "");
    setIndependentReason(selectedTank.independentReason ?? "");
  }, [selectedTank]);

  const circuitNameById = useMemo(
    () =>
      new Map(snapshot?.circuits.map((circuit) => [circuit.circuitId, circuit.displayName]) ?? []),
    [snapshot?.circuits],
  );

  function clearCircuitEditor() {
    setEditingCircuitId(undefined);
    setCircuitCode("");
    setDisplayName("");
    setCircuitKindValue("shared_sump");
    setNominalVolume("");
    setCircuitActive(true);
  }

  function editCircuit(circuit: WaterCircuitAdminRow) {
    setEditingCircuitId(circuit.circuitId);
    setCircuitCode(circuit.circuitCode);
    setDisplayName(circuit.displayName);
    setCircuitKindValue(circuit.circuitKind);
    setNominalVolume(circuit.nominalVolumeL === undefined ? "" : String(circuit.nominalVolumeL));
    setCircuitActive(circuit.isActive);
    setMessage(undefined);
    setError(undefined);
  }

  function prepareCircuitSave() {
    setMessage(undefined);
    setError(undefined);

    if (!snapshot?.canManage) {
      setError("دسترسی مدیریت آب برای این حساب فعال نیست.");
      return;
    }

    const existing = editingCircuitId
      ? snapshot.circuits.find((circuit) => circuit.circuitId === editingCircuitId)
      : undefined;
    const circuitId = existing?.circuitId ?? crypto.randomUUID();
    const volume = nominalVolume.trim() ? Number(nominalVolume) : undefined;

    try {
      const operation = buildWaterCircuitUpsertOperation(
        {
          actorId: userId,
          farmId,
          deviceId: getOrCreateDeviceId(),
          performedAt: new Date().toISOString(),
        },
        {
          circuitId,
          circuitCode,
          displayName,
          circuitKind: circuitKindValue,
          ...(volume === undefined ? {} : { nominalVolumeL: volume }),
          isActive: circuitActive,
          expectedVersion: existing?.domainVersion ?? 0,
        },
      );

      setPending(operation);
      setConfirmationParts([
        existing ? "ویرایش مدار آب" : "ایجاد مدار آب",
        `کد: ${circuitCode.trim().toUpperCase()}`,
        `نام: ${displayName.trim()}`,
        `نوع: ${kindLabel(circuitKindValue)}`,
        `حجم اسمی: ${volume === undefined ? "نامشخص" : `${volume} لیتر`}`,
        circuitActive ? "وضعیت: فعال" : "وضعیت: غیرفعال",
        `نسخه مورد انتظار: ${existing?.domainVersion ?? 0}`,
      ]);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "اطلاعات مدار آب معتبر نیست.");
    }
  }

  function prepareTankProfileSave() {
    setMessage(undefined);
    setError(undefined);

    if (!snapshot?.canManage) {
      setError("دسترسی مدیریت آب برای این حساب فعال نیست.");
      return;
    }

    if (!selectedTank) {
      setError("یک تانک انتخاب کنید.");
      return;
    }

    try {
      const operation = buildTankWaterProfileSetOperation(
        {
          actorId: userId,
          farmId,
          deviceId: getOrCreateDeviceId(),
          performedAt: new Date().toISOString(),
        },
        {
          tankId: selectedTank.tankId,
          expectedVersion: selectedTank.domainVersion,
          mode: profileMode,
          ...(profileMode === "connected" ? { circuitId: profileCircuitId } : {}),
          ...(profileMode === "independent" && independentReason.trim()
            ? { independentReason: independentReason.trim() }
            : {}),
        },
      );

      setPending(operation);
      setConfirmationParts([
        "تغییر اتصال آب تانک",
        `تانک: ${selectedTank.tankCode}`,
        profileMode === "connected"
          ? `متصل به: ${circuitNameById.get(profileCircuitId) ?? "مدار انتخاب‌شده"}`
          : "حالت: مستقل",
        ...(profileMode === "independent" && independentReason.trim()
          ? [`دلیل استقلال: ${independentReason.trim()}`]
          : []),
        `نسخه مورد انتظار: ${selectedTank.domainVersion}`,
      ]);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "تنظیم آب تانک معتبر نیست.");
    }
  }

  async function confirmPending() {
    if (!pending) return;

    try {
      setBusy(true);
      setError(undefined);
      const result = await persistAndSyncOperation(
        supabase,
        pending,
        typeof navigator === "undefined" ? true : navigator.onLine !== false,
      );
      onSyncSummary?.(result.summary);
      setMessage(syncMessage(result.operation.status));
      setPending(undefined);

      if (result.operation.status === "synced") {
        clearCircuitEditor();
        await refresh();
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "ثبت تغییر مدار آب انجام نشد.");
    } finally {
      setBusy(false);
    }
  }

  if (loading && !snapshot) {
    return (
      <section className="section-card">
        <p>در حال بارگذاری مدارهای آب…</p>
      </section>
    );
  }

  return (
    <section className="section-card" aria-labelledby="water-topology-admin-title">
      <div className="section-title-row">
        <div>
          <p className="section-kicker">M4B2</p>
          <h2 id="water-topology-admin-title">آب و مدارها</h2>
        </div>
        <span className="count-pill">{snapshot?.circuits.length ?? 0} مدار</span>
      </div>

      <p className="section-note">
        اتصال تانک و مشخصات مدار با کنترل نسخه ثبت می‌شود. تغییرات نوشتنی فقط از صف پایدار و RPC مجاز
        عبور می‌کنند.
      </p>

      <div className="work-list">
        {(snapshot?.circuits ?? []).map((circuit) => (
          <div className="work-item pending" key={circuit.circuitId}>
            <span className="work-main">
              <strong>
                {circuit.circuitCode} · {circuit.displayName}
              </strong>
              <span>
                {kindLabel(circuit.circuitKind)} ·{" "}
                {circuit.nominalVolumeL === undefined
                  ? "حجم نامشخص"
                  : `${circuit.nominalVolumeL} لیتر`}
                {" · "}
                {circuit.connectedTankCount} تانک متصل · نسخه {circuit.domainVersion} ·{" "}
                {circuit.isActive ? "فعال" : "غیرفعال"}
              </span>
            </span>
            <button type="button" className="text-button" onClick={() => editCircuit(circuit)}>
              ویرایش
            </button>
          </div>
        ))}
      </div>

      <div className="manager-card">
        <div className="section-title-row">
          <div>
            <p className="section-kicker">مدار</p>
            <h3>{editingCircuitId ? "ویرایش مدار" : "مدار جدید"}</h3>
          </div>
          {editingCircuitId ? (
            <button type="button" className="text-button" onClick={clearCircuitEditor}>
              مدار جدید
            </button>
          ) : null}
        </div>

        <div className="operation-field-grid">
          <label>
            کد مدار
            <input
              value={circuitCode}
              disabled={Boolean(editingCircuitId)}
              onChange={(event) => setCircuitCode(event.target.value)}
              placeholder="S01"
            />
          </label>

          <label>
            نام مدار
            <input value={displayName} onChange={(event) => setDisplayName(event.target.value)} />
          </label>

          <label>
            نوع مدار
            <select
              value={circuitKindValue}
              onChange={(event) => setCircuitKindValue(event.target.value as WaterCircuitKind)}
            >
              <option value="shared_sump">سامپ مشترک</option>
              <option value="shared_loop">مدار مشترک</option>
              <option value="other">سایر</option>
            </select>
          </label>

          <label>
            حجم اسمی (لیتر)
            <input
              type="number"
              min="1"
              value={nominalVolume}
              onChange={(event) => setNominalVolume(event.target.value)}
              placeholder="اختیاری"
            />
          </label>

          <label>
            <input
              type="checkbox"
              checked={circuitActive}
              onChange={(event) => setCircuitActive(event.target.checked)}
            />
            مدار فعال است
          </label>
        </div>

        <button
          type="button"
          className="secondary-button"
          disabled={busy || !snapshot?.canManage}
          onClick={prepareCircuitSave}
        >
          بررسی و ثبت مدار
        </button>
      </div>

      <div className="manager-card">
        <p className="section-kicker">توپولوژی تانک</p>
        <h3>اتصال یا حالت مستقل</h3>

        <div className="operation-field-grid">
          <label>
            تانک
            <select
              value={selectedTankId}
              onChange={(event) => setSelectedTankId(event.target.value)}
            >
              <option value="">انتخاب تانک</option>
              {(snapshot?.tanks ?? []).map((tank) => (
                <option key={tank.tankId} value={tank.tankId}>
                  {tank.tankCode}
                </option>
              ))}
            </select>
          </label>

          <label>
            وضعیت آب
            <select
              value={profileMode}
              onChange={(event) => setProfileMode(event.target.value as TankWaterMode)}
            >
              <option value="connected">متصل به مدار</option>
              <option value="independent">مستقل</option>
            </select>
          </label>

          {profileMode === "connected" ? (
            <label>
              مدار
              <select
                value={profileCircuitId}
                onChange={(event) => setProfileCircuitId(event.target.value)}
              >
                <option value="">انتخاب مدار</option>
                {(snapshot?.circuits ?? [])
                  .filter((circuit) => circuit.isActive)
                  .map((circuit) => (
                    <option key={circuit.circuitId} value={circuit.circuitId}>
                      {circuit.circuitCode} · {circuit.displayName}
                    </option>
                  ))}
              </select>
            </label>
          ) : (
            <label>
              دلیل استقلال
              <input
                value={independentReason}
                onChange={(event) => setIndependentReason(event.target.value)}
                placeholder="مثلاً قرنطینه"
              />
            </label>
          )}
        </div>

        {selectedTank ? (
          <p className="section-note">
            وضعیت فعلی:{" "}
            {selectedTank.mode === "connected"
              ? `متصل به ${circuitNameById.get(selectedTank.circuitId ?? "") ?? "مدار نامشخص"}`
              : selectedTank.mode === "independent"
                ? `مستقل${selectedTank.independentReason ? ` · ${selectedTank.independentReason}` : ""}`
                : "هنوز ثبت نشده"}
            {" · "}نسخه {selectedTank.domainVersion}
          </p>
        ) : null}

        <button
          type="button"
          className="secondary-button"
          disabled={busy || !snapshot?.canManage || !selectedTank}
          onClick={prepareTankProfileSave}
        >
          بررسی و ثبت اتصال تانک
        </button>
      </div>

      {snapshot?.tanks.length ? (
        <div className="work-list">
          {snapshot.tanks.map((tank) => (
            <div className="work-item pending" key={tank.tankId}>
              <span className="work-main">
                <strong>{tank.tankCode}</strong>
                <span>
                  {tank.mode === "connected"
                    ? `متصل · ${circuitNameById.get(tank.circuitId ?? "") ?? "مدار نامشخص"}`
                    : tank.mode === "independent"
                      ? `مستقل${tank.independentReason ? ` · ${tank.independentReason}` : ""}`
                      : "وضعیت آب ثبت نشده"}
                  {" · "}نسخه {tank.domainVersion}
                </span>
              </span>
            </div>
          ))}
        </div>
      ) : null}

      {message ? <p className="operation-success">{message}</p> : null}
      {error ? <p className="settings-warning">{error}</p> : null}

      {pending ? (
        <ConfirmationSheet
          title="تأیید تنظیم آب"
          parts={confirmationParts}
          speechEnabled
          fastConfirmEnabled
          onCancel={() => setPending(undefined)}
          onConfirm={() => void confirmPending()}
        />
      ) : null}
    </section>
  );
}
